import { defineStore } from 'pinia';
import integrationTypeConsts from '@/components/integrations/integrationTypeConsts';
import IntegrationService from '@/services/integrations';
import SettingService from '@/services/settings';
import _ from 'lodash';
// Reserved field for root Configurations in provider setting/config
import { _rootConfig } from '@/components/integrations/integrationConstants';
import { useBotsStore } from '@/stores/bots';

/** A single provider/webhook configuration object (shape defined by each provider). */
type IntegrationConfig = Record<string, any>;

interface IntegrationsState {
  /** Available integration types fetched from the API (with name/prio/...) */
  integrationTypes: any[];
  /** Language the cached integrationTypes were fetched in */
  integrationTypesLanguage: string | null;
  /** The integration type selected in the UI; null when none */
  selectedIntegrationType: any | null;
  /** Saved webhook configs keyed by webhook key */
  webhookConfigs: Record<string, IntegrationConfig>;
  /** Locally created/edited (unsaved) webhook configs keyed by webhook key */
  webhookConfigsLocal: Record<string, IntegrationConfig>;
  // Provider configs stores all providers. F.e. providerConfigs: { freshchat: {...}, livechat: {...} }
  /** Provider name currently being configured; null when none */
  currentProvider: string | null;
  /** Saved provider configs: provider name -> config-version-key -> config */
  providerConfigs: Record<string, Record<string, IntegrationConfig>>;
  /** Locally created/edited (unsaved) provider configs: provider name -> key -> config */
  providerConfigsLocal: Record<string, Record<string, IntegrationConfig>>;
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/integrations.ts` (namespace
 * `integrations`). Persisted to `sessionStorage` (full state) — matches the legacy `vuexSession`
 * reducer's `integrations: state.integrations` entry in `src/store/index.ts`.
 *
 * Collisions & renames (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`):
 * - State prop `integrationTypes` collided with getter `integrationTypes` -> getter renamed
 *   `integrationTypesGetter`.
 * - State prop `selectedIntegrationType` collided with the identity-passthrough getter of the
 *   same name -> getter renamed `selectedIntegrationTypeGetter`.
 * - State prop `webhookConfigs` collided with the identity-passthrough getter of the same name ->
 *   getter renamed `webhookConfigsGetter`.
 * - State prop `webhookConfigsLocal` collided with the identity-passthrough getter of the same
 *   name -> getter renamed `webhookConfigsLocalGetter`.
 * - State prop `providerConfigs` collided with getter `providerConfigs` (indexes into
 *   `state.providerConfigs[currentProvider]`, lazily initializing it) -> getter renamed
 *   `providerConfigsGetter`.
 * - State prop `providerConfigsLocal` collided with getter `providerConfigsLocal` (same shape as
 *   above) -> getter renamed `providerConfigsLocalGetter`.
 * - All 8 legacy mutations (`set`, `setSelectedIntegrationType`, `setWebhookConfig`,
 *   `setWebhookConfigLocal`, `deleteWebhookConfigLocal`, `setProviderConfig`,
 *   `setProviderConfigLocal`, `deleteProviderConfigLocal`) become same-named actions — none of
 *   them collided with an existing action name, so no merges were needed.
 * - `formatConfigsObjectToStandard` dropped its unused Vuex `context` first argument (it was
 *   never read in the body) — now takes just `(config)`.
 * - `rootGetters['bots/currentBotId']` -> `useBotsStore().currentBotId`, called inside each
 *   action/getter body (never at module top level).
 */
export const useIntegrationsStore = defineStore('integrations', {
  state: (): IntegrationsState => ({
    integrationTypes: [],
    integrationTypesLanguage: null,
    selectedIntegrationType: null,
    webhookConfigs: {},
    webhookConfigsLocal: {},
    // Provider configs stores all providers. F.e. providerConfigs: { freshchat: {...}, livechat: {...} }
    currentProvider: null,
    providerConfigs: {},
    providerConfigsLocal: {},
  }),
  getters: {
    /**
     * Renamed from legacy getter `integrationTypes` (collides with the state prop of the same
     * name in Pinia's single registry). Marks types not present in `integrationTypeConsts` as
     * `comingSoon`, and sorts by `prio`.
     */
    integrationTypesGetter(): any[] {
      let types = [...this.integrationTypes];
      types = types.map((t) => {
        // not in const = not ready to use
        if (!(integrationTypeConsts as any)[t.name]) t.comingSoon = true;
        return t;
      });

      types.sort(function (a, b) {
        if (a.prio < b.prio) {
          return -1;
        }
        if (a.prio > b.prio) {
          return 1;
        }
        return 0;
      });

      return types;
    },
    /** Renamed from legacy getter `selectedIntegrationType` (identity passthrough of the state prop of the same name). */
    selectedIntegrationTypeGetter(): any | null {
      return this.selectedIntegrationType;
    },
    /** Renamed from legacy getter `webhookConfigs` (identity passthrough of the state prop of the same name). */
    webhookConfigsGetter(): Record<string, IntegrationConfig> {
      return this.webhookConfigs;
    },
    webhookConfigsList(): IntegrationConfig[] {
      const keys = Object.keys(this.webhookConfigs);
      const configsList = keys.map((k) => {
        let config: IntegrationConfig = {
          ...this.webhookConfigs[k],
          key: k,
        };

        if (!config.displayName) {
          config.displayName = k;
        }

        return config;
      });
      return configsList || [];
    },
    /** Renamed from legacy getter `webhookConfigsLocal` (identity passthrough of the state prop of the same name). */
    webhookConfigsLocalGetter(): Record<string, IntegrationConfig> {
      return this.webhookConfigsLocal;
    },
    webhookConfigsListLocal(): IntegrationConfig[] {
      const keys = Object.keys(this.webhookConfigsLocal);
      const configsList = keys.map((k) => ({
        ...this.webhookConfigsLocal[k],
        key: k,
      }));
      return configsList || [];
    },
    webhookConfigsListSorted(): IntegrationConfig[] {
      let configList = this.webhookConfigsList.filter((c: IntegrationConfig) => {
        // Don't include local manipulated configs
        return !this.webhookConfigsListLocal.find((lc: IntegrationConfig) => lc.key === c.key);
      });

      if (this.webhookConfigsListLocal.length) {
        const localConfigList = this.webhookConfigsListLocal.map((config: IntegrationConfig) => {
          return {
            ...config,
            local: true,
          };
        });
        configList = [...configList, ...localConfigList];
      }
      configList.sort(function (a: IntegrationConfig, b: IntegrationConfig) {
        if (a.key < b.key) {
          return -1;
        }
        if (a.key > b.key) {
          return 1;
        }
        return 0;
      });
      return configList;
    },
    localWebhookConfigByKey(): (key: string) => IntegrationConfig | null {
      return (key: string) => {
        const localConfig = this.webhookConfigsLocal[key] || null;
        if (localConfig) return localConfig;

        return null;
      };
    },

    /**
     * Renamed from legacy getter `providerConfigs` (collides with the state prop of the same
     * name). Lazily initializes `state.providerConfigs[currentProvider]` as a side effect, exactly
     * like the legacy getter did.
     */
    providerConfigsGetter(): Record<string, IntegrationConfig> {
      const provider = this.currentProvider as string;
      if (!this.providerConfigs[provider]) {
        this.providerConfigs[provider] = {};
      }
      return this.providerConfigs[provider];
    },
    /**
     * Renamed from legacy getter `providerConfigsLocal` (collides with the state prop of the same
     * name). Lazily initializes `state.providerConfigsLocal[currentProvider]` as a side effect,
     * exactly like the legacy getter did.
     */
    providerConfigsLocalGetter(): Record<string, IntegrationConfig> {
      const provider = this.currentProvider as string;
      if (!this.providerConfigsLocal[provider]) {
        this.providerConfigsLocal[provider] = {};
      }
      return this.providerConfigsLocal[provider];
    },
    providerConfigVersionList(): IntegrationConfig[] {
      // pre-existing pattern: calls the store's own getter/action directly via `this`
      const providerConfig = this.providerConfigsGetter;
      // Pinia's getter `this` type only includes state + getters, not actions (see
      // pinia.d.ts `ThisType<... & _StoreWithGetters<G> & ...>` on the `getters` option) — so
      // `formatConfigsObjectToStandard` (an action) isn't visible on `this` here even though it
      // exists at runtime on the same store instance. Cast preserves the exact legacy call
      // (`actions.formatConfigsObjectToStandard({}, providerConfig)`).
      const configurations = (this as any).formatConfigsObjectToStandard(providerConfig);
      const configsList: IntegrationConfig[] = [];

      Object.keys(configurations).forEach((key) => {
        // At this step we sure that each value is an object, because of formatConfigsObjectToStandard
        const config = { ...configurations[key], key };

        if (!config.displayName) config.displayName = key;
        configsList.push(config);
      });

      return configsList;
    },
    providerConfigVersionListLocal(): IntegrationConfig[] {
      const localConfigs = this.providerConfigsLocalGetter;
      return Object.keys(localConfigs).map((k) => ({
        ...localConfigs[k],
        key: k,
      }));
    },
    providerConfigVersionsListSorted(): IntegrationConfig[] {
      const configList = this.providerConfigVersionList.filter((c: IntegrationConfig) => {
        // Don't include local manipulated configs
        return !this.providerConfigVersionListLocal.find((lc: IntegrationConfig) => lc.key === c.key);
      });
      if (this.providerConfigVersionListLocal.length) {
        this.providerConfigVersionListLocal.forEach(
          (config: IntegrationConfig) => configList.push({ ...config, local: true }),
        );
      }
      configList.sort((a: IntegrationConfig, b: IntegrationConfig) => {
        // '_rootConfig' and 'default' should be always first
        if (a.key === _rootConfig || b.key === _rootConfig) return a.key === _rootConfig ? -1 : 1;
        if (a.key === 'default' || b.key === 'default') return a.key === 'default' ? -1 : 1;
        return a.key > b.key
          ? 1
          : a.key < b.key ? -1 : 0;
      });
      return configList;
    },
    localProviderConfigByKey(): ({ provider, key }: { provider: string; key: string }) => IntegrationConfig | null {
      return ({ provider, key }: { provider: string; key: string }) => {
        const providerConfig = this.providerConfigsLocal[provider] || {};
        return providerConfig[key] || null;
      };
    },
  },
  actions: {
    async fetchIntegrationTypes({ language }: { language?: string }): Promise<void> {
      if (!language) language = 'de';
      // Already fetched in this language
      if (this.integrationTypes.length && this.integrationTypesLanguage === language) return;
      const allTypes: any = await IntegrationService.getAllTypes(language);

      if (allTypes?.items) {
        this.set({ key: 'integrationTypes', value: allTypes.items });
        this.set({ key: 'integrationTypesLanguage', value: language });
      }
    },
    async fetchWebhookConfigs(): Promise<any> {
      // currentBotId is `string | null`; legacy `rootGetters['bots/currentBotId']` was untyped
      // (`any`) so this same possibly-null value flowed into these calls at runtime already —
      // cast preserves that exact behavior while satisfying the now-strict param types.
      const botId = useBotsStore().currentBotId as string;
      const notifications: any = await SettingService.getSettings(
          botId,
          'notifications',
      );

      if (!notifications.webhook) {
        this.set({ key: 'webhookConfigs', value: [] });
        return [];
      }

      this.set({ key: 'webhookConfigs', value: notifications.webhook });
      return notifications.webhook;
    },
    async fetchWebhookConfigByKey(key: string): Promise<any> {
      const webhookConfigs = await this.fetchWebhookConfigs();
      return webhookConfigs?.[key] || null;
    },
    async createNewWebhookLocal(newName: string): Promise<IntegrationConfig> {
      const key = `${integrationTypeConsts.webhook_push}_${Date.now()}`;

      let allWebhookConfigs = [
        ...this.webhookConfigsList,
        ...this.webhookConfigsListLocal,
      ];
      // Find existing webhooks with new webhook
      allWebhookConfigs = allWebhookConfigs.filter((c) =>
          c.displayName.includes(newName),
      );

      const config = {
        key,
        displayName: `${newName} ${allWebhookConfigs.length + 1}`,
        method: 'post',
        url: '',
      };

      this.setWebhookConfigLocal(config);
      return config;
    },
    async saveWebhookConfig({ config, key }: { config: IntegrationConfig; key: string }): Promise<IntegrationConfig | false | undefined> {
      // currentBotId is `string | null`; legacy `rootGetters['bots/currentBotId']` was untyped
      // (`any`) so this same possibly-null value flowed into these calls at runtime already —
      // cast preserves that exact behavior while satisfying the now-strict param types.
      const botId = useBotsStore().currentBotId as string;
      const notifications: any = await SettingService.getSettings(
          botId,
          'notifications',
      );

      if (!notifications.webhook) {
        notifications.webhook = {};
      }

      notifications.webhook[key] = config;

      const result: any = await SettingService.setSettings(
          botId,
          notifications,
          'notifications',
      );

      if (!result) return;

      this.setWebhookConfig(config);
      this.deleteWebhookConfigLocal(key);

      return result?.webhook ? config : false;
    },
    async deleteWebhookConfig(key: string): Promise<boolean | undefined> {
      // currentBotId is `string | null`; legacy `rootGetters['bots/currentBotId']` was untyped
      // (`any`) so this same possibly-null value flowed into these calls at runtime already —
      // cast preserves that exact behavior while satisfying the now-strict param types.
      const botId = useBotsStore().currentBotId as string;
      const notifications: any = await SettingService.getSettings(
          botId,
          'notifications',
      );

      delete notifications.webhook[key];

      const result: any = await SettingService.setSettings(
          botId,
          notifications,
          'notifications',
      );

      if (!result?.webhook) return;
      this.deleteWebhookConfigLocal(key);

      return !!result?.webhook;
    },

    initProvider({ provider }: { provider: string }): string {
      // Initialize currentProvider.provider
      this.set({ key: 'currentProvider', value: provider });

      // We don't pass the config, because we want to only initialize provider name in state.providerConfigs[provider] = {}
      if (!this.providerConfigs[provider]) {
        this.setProviderConfig({ provider });
      }
      if (!this.providerConfigsLocal[provider]) {
        this.setProviderConfigLocal({ provider });
      }
      return provider;
    },
    // General for Livechat (as feature) Integration
    async createNewProviderLocal({ provider, config }: { provider: string; config: IntegrationConfig }): Promise<IntegrationConfig> {
      let allConfigs = [
        ...this.providerConfigVersionList,
        ...this.providerConfigVersionListLocal,
      ];

      const hasDefaultConfig = allConfigs?.some((config) => ['default', 'newConfig_default'].includes(config.key));

      // Find existing webhooks with new webhook
      allConfigs = allConfigs.filter((c) => c.displayName.includes(config?.displayName));

      const key = `newConfig_${Date.now()}`;
      const configuration: IntegrationConfig = {
        ...config,
        key,
      };

      if (hasDefaultConfig) {
        configuration.displayName = `${config.displayName} ${allConfigs.length + 1}`;
      } else {
        // Init SetUp Default config
        configuration.key = `newConfig_default`;
        configuration.displayName = `default`;
      }

      this.setProviderConfigLocal({ provider, config: configuration });
      return configuration;
    },
    async getProvidersVersionsOverview(provider: string): Promise<any> {
      // currentBotId is `string | null`; legacy `rootGetters['bots/currentBotId']` was untyped
      // (`any`) so this same possibly-null value flowed into these calls at runtime already —
      // cast preserves that exact behavior while satisfying the now-strict param types.
      const botId = useBotsStore().currentBotId as string;
      const res: any = await SettingService.getSettingsOverview(botId);

      if (provider && res[provider]) return res[provider];
      return res;
    },
    /**
     *
     * @param {{[key:string]: any}} config
     * So far as the config must have one standard, where each key in bot.settings.provider should be valid config-version-object.
     * This function formats this to Standard to Manage this config. Field key ('version') will be added inside its config.
     * Each key that is not an object will be assigned to _badConfig key or _rootConfig.
     * We will match keys also to identify properties which were in root object
     * @returns {{[key:string]: any}} Valid config where each field is and object.
     */
    formatConfigsObjectToStandard(config: Record<string, any>): Record<string, IntegrationConfig> {
      const configVersionRegex = /^v_\d+$/;
      const formattedConfigs: Record<string, IntegrationConfig> = {};
      const rootConfig: IntegrationConfig = { key: _rootConfig, displayName: 'General Settings' };
      Object.keys(config).forEach((key) => {
        const value = config[key];

        // Check if it is possible basic config property (nested object) | Like 'case' for salesforce { ..., case: {} }, like 'ticket' for zendesk { ..., ticket: {} }
        let isConfigProperty = false;

        if (_.isObject(value)) {
          if (!(value as any).displayName) isConfigProperty = true;
          if (configVersionRegex.test(key) || key === _rootConfig) isConfigProperty = false; // in case if proper version doesn't include displayName
        }

        // If not object
        if (isConfigProperty || !_.isObject(value) || Array.isArray(value)) {
          rootConfig[key] = value;
          return;
        }

        const formattedConfig: Record<string, any> = { ...(value as Record<string, any>), key };
        if (!formattedConfig.displayName) formattedConfig.displayName = key;
        formattedConfigs[key] = formattedConfig;
      });

      // If there is only one field { key } => there is no reason to show it atm
      if (Object.keys(rootConfig).length > 2) formattedConfigs[_rootConfig] = rootConfig;
      return formattedConfigs;
    },
    /**
     * @param {{
     *  provider: string,
     *  key: string,
     * }} options If key not pass, then the whole config will returned. Field 'key' is a config 'version'.
     * @returns
     */
    async fetchProviderVersions({ provider, key = null }: { provider: string; key?: string | null }): Promise<any> {
      // currentBotId is `string | null`; legacy `rootGetters['bots/currentBotId']` was untyped
      // (`any`) so this same possibly-null value flowed into these calls at runtime already —
      // cast preserves that exact behavior while satisfying the now-strict param types.
      const botId = useBotsStore().currentBotId as string;

      let configurations: any = await SettingService.getProviderSettings({ botId, provider });
      // Will return 'null' if the bot does not have this provider, which can be the case when you first set it up
      if (configurations === null) configurations = {};
      else configurations = this.formatConfigsObjectToStandard(configurations);

      const allProviders = this.providerConfigs;
      allProviders[provider] = configurations || {};

      this.set({ key: 'providerConfigs', value: allProviders });

      // Get Bot Config for Provider by key - version
      if (key) return configurations[key];
      return configurations;
    },
    async fetchProviderConfigByKey({ provider, key }: { provider: string; key: string }): Promise<any> {
      const configs = await this.fetchProviderVersions({ provider });
      return configs?.[key] || null;
    },
    async checkIfProviderExists({ provider }: { provider: string }): Promise<boolean> {
      // currentBotId is `string | null`; legacy `rootGetters['bots/currentBotId']` was untyped
      // (`any`) so this same possibly-null value flowed into these calls at runtime already —
      // cast preserves that exact behavior while satisfying the now-strict param types.
      const botId = useBotsStore().currentBotId as string;
      const configs = await SettingService.getProviderSettings({ botId, provider });
      return !!configs;
    },
    /**
     *
     * @param {{
     *  provider: string,
     *  key: string,
     *  newKey: string,
     *  config: any,
     * }} param0. Where 'key' is a config 'version'. config - is valid provider config.
     * Provide newKey name if you want rename the version name of config.
     * @returns { Promise<{ config: any|false, key: string }>}
     */
    async saveProviderConfig({ provider, key, config, isProviderSetuped }: { provider: string; key: string; config: IntegrationConfig; isProviderSetuped?: boolean }): Promise<{ config: IntegrationConfig | false; key: string } | undefined> {
      // currentBotId is `string | null`; legacy `rootGetters['bots/currentBotId']` was untyped
      // (`any`) so this same possibly-null value flowed into these calls at runtime already —
      // cast preserves that exact behavior while satisfying the now-strict param types.
      const botId = useBotsStore().currentBotId as string;
      const isNew = key.startsWith('newConfig_');
      const isNewDefault = key === 'newConfig_default';

      const keyId = isNew
        ? !isNewDefault ? `v_${Date.now()}` : 'default'
        : key;

      const result = isNew
          ? await SettingService.addProviderVersionSettings({ botId, provider, key: keyId, config })
          : await SettingService.updateProviderVersionSettings({ botId, provider, key: keyId, config });

      if (!result) return;

      this.setProviderConfig({ provider, config });
      this.deleteProviderConfigLocal({ provider, key });

      return { config: config || false, key: keyId };
    },
    async setupProviderConfig({ provider, config }: { provider: string; config: IntegrationConfig }): Promise<{ success: boolean; data?: any; error?: any } | false> {
      // currentBotId is `string | null`; legacy `rootGetters['bots/currentBotId']` was untyped
      // (`any`) so this same possibly-null value flowed into these calls at runtime already —
      // cast preserves that exact behavior while satisfying the now-strict param types.
      const botId = useBotsStore().currentBotId as string;
      config.displayName = 'default';

      const result = await SettingService.setProviderSettings({
        botId,
        provider,
        config: {
          default: config,
        },
      });

      // Set key as default for cache
      config.key = 'default';
      this.setProviderConfig({ provider, config });
      return result || false;
    },
    async deleteProviderConfig({ provider, key }: { provider: string; key: string }): Promise<boolean> {
      // currentBotId is `string | null`; legacy `rootGetters['bots/currentBotId']` was untyped
      // (`any`) so this same possibly-null value flowed into these calls at runtime already —
      // cast preserves that exact behavior while satisfying the now-strict param types.
      const botId = useBotsStore().currentBotId as string;
      const result = await SettingService.deleteProviderVersionSettings({
        botId, provider, key,
      });

      // If success => true
      return result === true;
    },

    // --- Converted from legacy mutations (same names — none collided with an existing action) ---

    set(payload: { key: string; value: any }) {
      // generic key/value setter — key is dynamic, so index untyped
      (this as any)[payload.key] = payload.value;
    },
    setSelectedIntegrationType(value: any) {
      this.selectedIntegrationType = value;
    },
    setWebhookConfig(config: IntegrationConfig) {
      const newWebhookConfigs = { ...this.webhookConfigs };
      newWebhookConfigs[config.key] = config;
      this.webhookConfigs = newWebhookConfigs;
    },
    setWebhookConfigLocal(config: IntegrationConfig) {
      const newWebhookConfigsLocal = { ...this.webhookConfigsLocal };
      newWebhookConfigsLocal[config.key] = config;
      this.webhookConfigsLocal = newWebhookConfigsLocal;
    },
    deleteWebhookConfigLocal(key: string) {
      const newWebhookConfigsLocal = { ...this.webhookConfigsLocal };
      delete newWebhookConfigsLocal[key];
      this.webhookConfigsLocal = newWebhookConfigsLocal;
    },

    setProviderConfig({ provider, config }: { provider: string; config?: IntegrationConfig }) {
      if (!provider) return;
      const allProviders = { ...this.providerConfigs };
      if (!allProviders[provider]) allProviders[provider] = {}; // if !config => init providerConfigs.provider
      if (config) allProviders[provider][config.key] = config;
      this.providerConfigs = allProviders;
    },
    setProviderConfigLocal({ provider, config }: { provider: string; config?: IntegrationConfig }) {
      if (!provider) return;
      const allProviderslocal = { ...this.providerConfigsLocal };
      if (!allProviderslocal[provider]) allProviderslocal[provider] = {};
      if (config) allProviderslocal[provider][config.key] = config;
      this.providerConfigsLocal = allProviderslocal;
    },
    deleteProviderConfigLocal({ provider, key }: { provider: string; key: string }) {
      if (!provider) return;
      const allProviderslocal = { ...this.providerConfigsLocal };
      delete allProviderslocal?.[provider]?.[key];
      this.providerConfigsLocal = allProviderslocal;
    },
  },
  vuexPersist: {
    session: true,
  },
});
